import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import Step1Earning from './Step1Earning';
import Step2Username from './Step2Username';
import Step3ProfilePicture from './Step3ProfilePicture';
import Step4Interests from './Step4Interests';
import Step5About from './Step5About';
import Step6Payment from './Step6Payment';
import CreatorPreview from './CreatorPreview';

const CreatorSetup = ({ onComplete, onSkip, onEdit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    earningMethods: [],
    username: '',
    profilePicture: null,
    interests: [],
    displayName: '',
    bio: '',
    paymentConnected: false
  });

  const totalSteps = 6;

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(formData);
  };

  const handleEdit = (stepNumber) => {
    if (onEdit) {
      onEdit(formData);
    }
    setCurrentStep(stepNumber);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Earning
            earningMethods={formData.earningMethods}
            onUpdate={updateFormData}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <Step2Username
            username={formData.username}
            onUpdate={updateFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <Step3ProfilePicture
            profilePicture={formData.profilePicture}
            onUpdate={updateFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        return (
          <Step4Interests
            interests={formData.interests}
            onUpdate={updateFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 5:
        return (
          <Step5About
            displayName={formData.displayName}
            bio={formData.bio}
            onUpdate={updateFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 6:
        return (
          <Step6Payment
            paymentConnected={formData.paymentConnected}
            onUpdate={updateFormData}
            onComplete={handleComplete}
            onBack={prevStep}
            onSkip={onSkip}
          />
        );
      default:
        return null;
    }
  };

  return (
    <SetupContainer>
      <SetupHeader>
        <Logo>
          <LogoIcon>☕</LogoIcon>
          <LogoText>BUCX</LogoText>
        </Logo>
        
        <ProgressBar>
          {Array.from({ length: totalSteps }, (_, index) => (
            <ProgressStep
              key={index + 1}
              $active={currentStep >= index + 1}
              $completed={currentStep > index + 1}
            >
              {currentStep > index + 1 ? '✓' : index + 1}
            </ProgressStep>
          ))}
        </ProgressBar>
      </SetupHeader>

      <SetupContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </SetupContent>
    </SetupContainer>
  );
};

const SetupContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const SetupHeader = styled.div`
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e0c16f;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoIcon = styled.span`
  font-size: 2rem;
  color: #e0c16f;
`;

const LogoText = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ProgressStep = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  background: ${props => 
    props.$completed ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : 
    props.$active ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : '#333333'
  };
  color: ${props => props.$completed || props.$active ? '#000000' : '#666666'};
  border: 2px solid ${props => props.$active ? '#e0c16f' : '#333333'};
`;

const SetupContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

export default CreatorSetup;
